import { intlLocale, t } from '../i18n'

export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export function tzLabel(tz: string = browserTimeZone(), at: Date = new Date()): string {
  return new Intl.DateTimeFormat(intlLocale(), { timeZone: tz, timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? tz
}

const LOCALE = intlLocale()

export const formatDateTime = (iso: string, o: { timeZone?: string } = {}) =>
  new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short', timeZone: o.timeZone }).format(new Date(iso))

export const formatTime = (iso: string, o: { timeZone?: string; seconds?: boolean } = {}) =>
  new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', second: o.seconds ? '2-digit' : undefined, timeZone: o.timeZone }).format(
    new Date(iso),
  )

export const formatDate = (d: Date | string, o: { timeZone?: string } = {}) =>
  new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeZone: o.timeZone }).format(typeof d === 'string' ? new Date(d) : d)

export const isToday = (d: Date) => toDateInputValue(d) === toDateInputValue(new Date())

export const fmtSmartTime = (iso: string) => (isToday(new Date(iso)) ? formatTime(iso) : formatDateTime(iso))

export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.floor(minutes))
  if (m < 60) return t('lib.durationMinutes', { m })
  return t('lib.durationHours', { h: Math.floor(m / 60), mm: String(m % 60).padStart(2, '0') })
}

export function relativeAgo(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return t('lib.agoSeconds', { n: s })
  if (s < 3600) return t('lib.agoMinutes', { n: Math.floor(s / 60) })
  return t('lib.agoHours', { n: Math.floor(s / 3600) })
}

export function startOfDay(d: Date): Date {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  return r
}
export function endOfToday(d: Date): Date {
  const r = new Date(d)
  r.setHours(23, 59, 59, 999)
  return r
}
export function startOfWeekMonday(d: Date): Date {
  const r = startOfDay(d)
  r.setDate(r.getDate() - ((r.getDay() + 6) % 7))
  return r
}
export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)

export const toDateInputValue = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const fromDateInputValue = (s: string, endOfDay = false): Date => {
  const d = new Date(`${s}T00:00:00`)
  return endOfDay ? endOfToday(d) : d
}

/** How far `tz`'s wall clock is ahead of UTC at instant `at` (ms since epoch), in ms. */
function zoneOffsetMs(at: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(at))
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)!.value)
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - Math.floor(at / 1000) * 1000
}

/** The UTC instant (ISO) for a wall-clock date ('YYYY-MM-DD') and time ('HH:mm') in IANA zone `tz`, so a
 * shift is entered in the site's time, whatever the browser's zone. Matches the backend's series expansion:
 * a time skipped by a daylight-saving jump lands an hour later, and a repeated one resolves to standard time. */
export function zonedToUtcIso(date: string, time: string, tz: string): string {
  const [y, m, d] = date.split('-').map(Number)
  const [h, min] = time.split(':').map(Number)
  const wall = Date.UTC(y, m - 1, d, h, min)
  let utc = wall - zoneOffsetMs(wall, tz)
  const settled = zoneOffsetMs(utc, tz)
  if (wall - settled !== utc) utc = wall - settled
  return new Date(utc).toISOString()
}

/** The wall-clock date ('YYYY-MM-DD') and time ('HH:mm') of `iso` in IANA zone `tz`. */
export function zonedParts(iso: string, tz: string): { date: string; time: string } {
  const offset = zoneOffsetMs(Date.parse(iso), tz)
  const local = new Date(Date.parse(iso) + offset)
  const pad = (n: number) => String(n).padStart(2, '0')
  return { date: `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`, time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}` }
}

/** 'YYYY-MM-DD' plus `n` calendar days, independent of any time zone. */
export function addDaysToDateString(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}
